import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { spring, useCurrentFrame, useVideoConfig } from "remotion";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";


interface SpatialInsightsProps {
  bullets: Array<{ text: string; highlights?: string[] }>;
  activeBulletIndex: number;
}

export const SpatialInsights: React.FC<SpatialInsightsProps> = ({
  bullets,
  activeBulletIndex,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Bullet entrance spring (triggers on index changes)
  const animScale = spring({
    frame: frame - activeBulletIndex * 15, // Delay off bullet activation frames
    fps,
    from: 0.94,
    to: 1.0,
    config: { damping: 13, stiffness: 100 },
  });

  const animTranslateY = spring({
    frame: frame - activeBulletIndex * 15,
    fps,
    from: 25,
    to: 0,
    config: { damping: 13, stiffness: 100 },
  });

  const activeBullet = bullets[activeBulletIndex] || bullets[0] || { text: "" };

  // DOT Index pagination tracker
  const renderPaginationDots = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          marginTop: "30px",
        }}
      >
        {bullets.map((_, idx) => {
          const isActive = idx === activeBulletIndex;
          return (
            <div
              key={idx}
              style={{
                width: isActive ? "32px" : "10px",
                height: "10px",
                borderRadius: "5px",
                backgroundColor: isActive
                  ? "#00f2fe"
                  : "rgba(255, 255, 255, 0.2)",
                boxShadow: isActive ? "0 0 10px #00f2fe" : "none",
                transition: "all 0.25s ease-in-out",
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div
      style={{
        width: "100%",
        padding: "45px 35px",
        background: "rgba(15, 10, 30, 0.75)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: "2px solid #00f2fe",
        borderRadius: "4px",
        boxShadow:
          "0 0 35px rgba(0, 242, 254, 0.25), inset 0 0 20px rgba(0, 242, 254, 0.15)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transform: `scale(${animScale}) translateY(${animTranslateY}px)`,
        transformOrigin: "center",
        minHeight: "330px",
      }}
    >
      {/* Sci-Fi Corner Decorations */}
      <div
        style={{
          position: "absolute",
          top: "-2px",
          left: "-2px",
          width: "24px",
          height: "24px",
          borderTop: "4px solid #ff007f",
          borderLeft: "4px solid #ff007f",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "-2px",
          right: "-2px",
          width: "24px",
          height: "24px",
          borderTop: "4px solid #ff007f",
          borderRight: "4px solid #ff007f",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-2px",
          left: "-2px",
          width: "24px",
          height: "24px",
          borderBottom: "4px solid #ff007f",
          borderLeft: "4px solid #ff007f",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-2px",
          right: "-2px",
          width: "24px",
          height: "24px",
          borderBottom: "4px solid #ff007f",
          borderRight: "4px solid #ff007f",
        }}
      />

      {/* Hologram Badge */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "30px" }}>
        <div
          style={{
            borderRadius: "2px",
            background: "rgba(0, 242, 254, 0.1)",
            border: "1px solid rgba(0, 242, 254, 0.3)",
            color: "#00f2fe",
            fontFamily: "'Orbitron', sans-serif",
            fontWeight: 800,
            fontSize: "12px",
            letterSpacing: "3px",
            textTransform: "uppercase",
            textShadow: "0 0 8px rgba(0, 242, 254, 0.5)",
            padding: "4px 10px",
          }}
        >
          🛰️ SYSTEM INSIGHT {activeBulletIndex + 1} //
        </div>
        {(() => {
          const brand = detectBrandInText(activeBullet.text);
          return brand ? (
            <CompanyBadge
              name={brand}
              color="#00f2fe"
              accentColor="#00f2fe"
              size={72}
              style="glow"
            />
          ) : null;
        })()}
      </div>

      {/* Large Mobile Takeaway Centering Wrapper */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <p
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "54px",
            fontWeight: 700,
            lineHeight: 1.4,
            color: "#ffffff",
            textAlign: "center",
            textShadow: "0 0 10px rgba(255, 255, 255, 0.15)",
            width: "100%",
          }}
        >
          <HighlightText
            text={activeBullet.text}
            highlights={activeBullet.highlights}
            highlightStyle={{
              fontWeight: 800,
              background: "linear-gradient(135deg, #00f2fe, #ff007f)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              display: "inline",
            }}
          />
        </p>
      </div>

      {/* Pagination Index Dots */}
      {renderPaginationDots()}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
